import { iconByProduct } from 'features/subscriptions/utils';
import { isTenantVisibleForScaiDamFlag } from 'features/common/utils/tenantLabels';
import { GetOrganizationTenantsQuery } from 'gql/graphql';
import * as Yup from 'yup';
import { CreateTeamFormValues } from './types';

export const USER_GROUPS_PERMISSIONS_INFO_MSG =
  'Please be aware that if a user is assigned to multiple groups, the higher level of access is applied.';

export const SELECT_AT_LEAST_ONE_APPLICATION_MSG =
  'Select at least one application to grant access.';

export const APP_ACCESS_REQUIRED_MSG = 'At least one app is required';

// Initial form values
export const initialValues: CreateTeamFormValues = {
  name: '',
  description: '',
  roles: [],
};

// Define the validation schema
export const createTeamValidationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .min(3, 'Name must be at least 3 characters')
    .max(100, 'Name must be no more than 100 characters')
    .required('Name is required'),
  description: Yup.string()
    .trim()
    .max(256, 'Description must be no more than 256 characters'),
  roles: Yup.array()
    .of(
      Yup.object({
        scope: Yup.string().required('Scope is required'),
        role: Yup.string().required('Role is required'),
      }),
    )
    .optional(),
});

export const transformTenants = (
  tenants?: GetOrganizationTenantsQuery,
  isScaiDamEnabled = false,
) => {
  if (!tenants?.organization?.applications?.nodes) return [];

  return tenants.organization.applications.nodes
    .filter((tenant) =>
      isTenantVisibleForScaiDamFlag(
        tenant.productCode,
        tenant.labels,
        isScaiDamEnabled,
      ),
    )
    .map((tenant) => {
      const { name, iconUrl } =
        iconByProduct[tenant.productCode.toLowerCase()] ?? {};

      return {
        title: name || tenant.productCode,
        subTitle: tenant.displayName || tenant.name,
        icon: {
          iconUrl: iconUrl || '',
        },
        systemId: tenant.systemId,
        productCode: tenant.productCode,
        id: tenant.id,
        organizationId: tenant.organizationId,
        value: tenant.id,
        roles:
          tenant.product?.roles?.nodes?.map((role) => ({
            value: role.name,
            label: role.name,
          })) ?? [],
      };
    })
    .filter((tenant) => tenant.roles.length > 0);
};

export const getHighlightedUsersError = (count: number): string =>
  count === 1
    ? 'The highlighted user is already part of this group'
    : 'The highlighted users are already part of this group';

export const getAddedToGroupSuccess = (count: number): string =>
  count === 1 ? 'User added to the group' : 'Users added to the group';

export const getAddedToGroupError = (count: number): string =>
  count === 1
    ? 'Failed to add user to the group'
    : 'Failed to add users to the group';

export const getPartialAddToGroupWarning = (
  successCount: number,
  totalCount: number,
): string =>
  `${successCount} of ${totalCount} users added to the group. ${
    totalCount - successCount
  } failed.`;

export const addMembersValidationSchema = Yup.object({
  members: Yup.array()
    .min(1, 'Add at least 1 user')
    .required('Add at least 1 user')
    .test('no-external-duplicates', function (members) {
      if (!Array.isArray(members)) return true;
      const highlightedCount = members.filter((m) => m.highlight).length;
      if (highlightedCount === 0) return true;
      return this.createError({
        message: getHighlightedUsersError(highlightedCount),
      });
    }),
});
